/**
 * "End this lesson?" — the confirmation in front of SRS I1 step 4.
 *
 * A lesson can only be ended once and every student is taken off the board when it
 * is, so it is worth one deliberate tap. The dialog stays up while the server works,
 * because the server does not answer until the board has been written for the last
 * time — and until it has, saying the lesson is finished would not be true.
 */

import { useEffect, useState } from 'react';

export interface EndSessionDialogProps {
  /** Ends the class. Resolves with a message when it could not be done. */
  readonly onConfirm: () => Promise<string | null>;
  readonly onCancel: () => void;
}

/** Closes on Escape, but never while the lesson is halfway through being filed. */
function useEscape(onCancel: () => void, ending: boolean): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !ending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onCancel, ending]);
}

interface ActionsProps {
  readonly ending: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function Actions({ ending, onConfirm, onCancel }: ActionsProps) {
  return (
    <div className="flex items-center justify-end gap-3 pt-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={ending}
        className="h-touch rounded-md border border-border bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover disabled:opacity-50"
      >
        Keep teaching
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={ending}
        className="h-touch rounded-md bg-danger px-6 text-base font-medium text-text-inverse transition duration-fast ease-out hover:opacity-90 disabled:opacity-50"
      >
        {ending ? 'Saving the lesson…' : 'End Session'}
      </button>
    </div>
  );
}

export function EndSessionDialog({ onConfirm, onCancel }: EndSessionDialogProps) {
  const [ending, setEnding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEscape(onCancel, ending);

  const confirm = (): void => {
    setEnding(true);
    setProblem(null);
    void onConfirm().then((message) => {
      // Nothing to do when it worked: the screen behind this one becomes the ended one.
      if (message === null) return;
      setEnding(false);
      setProblem(message);
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !ending) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-session-title"
        className="flex w-full max-w-[440px] flex-col gap-4 rounded-lg bg-surface p-8 shadow-xl"
      >
        <h2 id="end-session-title" className="text-xl font-semibold text-text-primary">
          End this lesson?
        </h2>
        <p className="text-base text-text-secondary">
          Everything on the board is saved and your students are taken off it. The room code stops
          working, so nobody can rejoin.
        </p>
        {problem === null ? null : <p className="text-base text-danger">{problem}</p>}
        <Actions ending={ending} onConfirm={confirm} onCancel={onCancel} />
      </div>
    </div>
  );
}
